import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Fab,
  Grid,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  Stepper,
  Step,
  StepLabel,
  Pagination,
  AccordionDetails,
  Chip,
  Link,
} from '@mui/material';
import api from '../../api/api';
import dayjs from 'dayjs';
import * as XLSX from "xlsx";

import ApproveRequestDialogManager from '../commonservices/ApproveRequestDialogManager';
import ApproveRequestDialogAdmin from '../commonservices/ApproveRequestDialogAdmin';

import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../contexts/AuthContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import SourceIcon from '@mui/icons-material/Source';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Person2Icon from '@mui/icons-material/Person2';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import InfoIcon from '@mui/icons-material/Info';
import DirectionsIcon from '@mui/icons-material/Directions';
import TimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FenceIcon from '@mui/icons-material/Fence';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';

const PAGE_SIZE = 5;

const CourierRequestListingPage = () => {

  var utc = require("dayjs/plugin/utc");
  dayjs.extend(utc);

  const navigate = useNavigate();
  const { user } = useAuth();
  const [userId, setUserId] = useState(null);
  const [role, setRole] = useState(null);
  const [page, setPage] = useState(1);

  const handlePageChange = (event, value) => {
    setPage(value);
  }


  useEffect(() => {
    if (user) {
      setUserId(user['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']);
      setRole(user['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
    }
  }, [user]);

  const [courierRequest, setCourierRequest] = useState([]);
  const [materialList, setMaterialList] = useState([]);

  const [filteredData, paginatedData] = useMemo(() => {
    const filteredData = courierRequest?.filter(
      req => req.employeeId == userId ||
        (role == "Manager" && req.approvalManagerId == userId) ||
        (role == "Admin" && req.managerApprovedAt)
    );
    return [filteredData, filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)];
  }, [userId, role, page, courierRequest])


  // APIs
  const loadCourierRequest = () => {
    api.get('/api/CourierRequests')
      .then(response => {
        setCourierRequest(response.data);
      })
      .catch(error => {
        console.log('Error fetching Courier List', error);
      });
  }

  useEffect(() => {
    loadTypeOfMaterialList();
    loadCourierRequest();
  }, []);

  const loadTypeOfMaterialList = () => {
    api.get('/api/CourierMaterialType')
      .then(response => {
        setMaterialList(response.data);
      })
      .catch(error => {
        console.log('Error fecthing Material List', error);
      });
  }

  const urgencyLevelList = [
    { id: 0, name: "Lowest" },
    { id: 1, name: "Low" },
    { id: 2, name: "Medium" },
    { id: 3, name: "High" },
    { id: 4, name: "Critical" },
  ];

  const getModeOfPickup = (modeOfPickUp) => {
    switch (modeOfPickUp) {
      case 0:
        return "Direct Pickup By Courier Office";
      case 1:
        return "Hand Over To Admin";
      default:
        return "Self";
    }
  };

  const getFileIcon = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();
    switch (extension) {
      case 'jpg':
        break;
      case 'jpeg':
        break;
      case 'png':
        break;
    }
  };

  //#region  Excel section
  const handleDownload = async (e) => {
    let wb = XLSX.utils.book_new(),
      pws = XLSX.utils.json_to_sheet([]);

    let courierRequestHeaders = [[
      "Request Id",
      "Requester",
      "Requested At",
      "From Location",
      "To Location",
      "Mode of Pickup",
      "Gate Pass Type",
      "Gate Pass Number",
      "Urgency Level",
      "Type of Material",
      "Courier Type",
      "Purpose of Requirement",
      "Approval Manager",
      "Manager Approved At",
      "Manager Rejected At",
      "Manager Remarks",
      "Admin Approved At",
      "Cancelled By",
      "Cancelled At",
      "Completed At"]];

    const courierRequestData = filteredData.map((c) => ({
      RequestId: c.requestId,
      Requester: c.employee?.name,
      RequestedAt: dayjs(c.createdAt).format("DD/MM/YYYY hh:mm a"),
      FromLocation: c.fromLocation,
      ToLocation: c.toLocation,
      ModeOfPickup: c.modeOfPickUp == 0 ? "Direct pickup by courier office" : c.modeOfPickUp == 1 ? "Handover to admin" : "Self",
      GatePassType: c.gatepassType == 0 ? "RGP" : "NRGP",
      GatePassNumber: c.gatepassNumber,
      UrgencyLevel: urgencyLevelList[c.urgencyLevel]?.name,
      TypeOfMaterial: materialList.length > 0 ? materialList.find(x => x.id == c.typeOfMaterialId)?.name : "",
      CourierType: c.courierType == 0 ? "Domestic" : "International",
      PurposeOfRequirement: c.purpose,
      ApprovalManager: c.approvalManager?.name,
      ManagerApprovedAt: c.managerApprovedAt && dayjs(c.managerApprovedAt).format("DD/MM/YYYY hh:mm a"),
      ManagerRejectedAt: c.managerRejectedAt && dayjs(c.managerRejectedAt).format("DD/MM/YYYY hh:mm a"),
      ManagerRemarks: c.managerRemarks,
      AdminApprovedAt: c.adminApprovedAt && dayjs(c.adminApprovedAt).format("DD/MM/YYYY hh:mm a"),
      CancelledBy: c.cancelledBy?.name,
      CancelledAt: c.CancelledAt && dayjs(c.CancelledAt).format("DD/MM/YYYY hh:mm a"),
      CompletedAt: c.completedAt && dayjs(c.completedAt).format("DD/MM/YYYY hh:mm a")
    }));

    XLSX.utils.sheet_add_aoa(pws, courierRequestHeaders);
    XLSX.utils.sheet_add_json(pws, courierRequestData, { origin: 'A2', skipHeader: true });

    XLSX.utils.book_append_sheet(wb, pws, "Courier Requests");

    XLSX.writeFile(wb, "Export.xlsx");
  }
  //#endregion

  //#region Manager Approval Section

  const [dialogOpen, setDialogOpen] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handleRejectOpenDialog = (request) => {
    setSelectedRequest(request);
    setDialogOpen('reject');
  };

  const handleCancelRequest = (request) => {

    request.cancelledAt = dayjs().utc('z').format();
    request.cancelledById = userId;

    api.put(`/api/CourierRequests/${request.id}`, request)
      .then(response => {
        loadCourierRequest();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error cancelling request', error);
        handleCloseDialog();
        // Handle error
      });
  };

  const handleCompleteRequest = (request) => {

    request.completedAt = dayjs().utc('z').format();

    api.put(`/api/CourierRequests/${request.id}`, request)
      .then(response => {
        loadCourierRequest();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error submitting request', error);
        handleCloseDialog();
        // Handle error
      });
  };

  const handleCloseDialog = () => {
    setDialogOpen(null);
    setSelectedRequest(null);
  };

  const handleApproveOpenDialog = (request) => {
    setSelectedRequest(request);
    setDialogOpen('approve');
  };

  const handleApproveManager = (remark) => {

    if (dialogOpen === 'approve') {
      selectedRequest.managerApprovedAt = dayjs().utc('z').format();
    } else {
      selectedRequest.managerRejectedAt = dayjs().utc('z').format();
    }

    selectedRequest.managerRemarks = remark;

    api.put(`/api/CourierRequests/${selectedRequest.id}`, selectedRequest)
      .then(response => {
        loadCourierRequest();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error approving request', error);
        handleCloseDialog();
        // Handle error
      });
  };

  const handleApproveAdmin = (courierTrackingNumber, courierCompany, hyperlinkForTracking, files) => {
    const formData = new FormData();
    
    // Append only required fields
    formData.append("id", selectedRequest.id);
    formData.append("adminApprovedAt", dayjs().utc('z').format());
    formData.append("courierTrackingNumber", courierTrackingNumber);
    formData.append("courierCompany", courierCompany);
    formData.append("hyperlinkForTracking", hyperlinkForTracking);

    // Append files if they exist
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });

    formData.forEach((value, key) => {
      console.log(`${key}:`, value);
  });
  

    // Send the request
    api.put('/api/CourierRequests/UpdateTrackingInfo', formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    })
    .then(response => {
        console.log("Request approved successfully", response);
        loadCourierRequest();
        handleCloseDialog();
    })
    .catch(error => {
        console.error("Error approving request", error);
        handleCloseDialog();
    });
};

  
  //#endregion

  return (

    <Box sx={{ p: 2 }}>
      <Box mb={2} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
        <Typography component="p" variant="h5">Courier Requirement</Typography>
        {
          <Button size='small' variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
            Excel
          </Button>
        }
      </Box>
      <Grid container spacing={2}>
        {paginatedData?.map((request) => (
          <Grid item xs={12} key={request.id}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${request.id}-content`}
                id={`panel${request.id}-header`}
              >
                <Box sx={{
                  width: '100%', display: 'grid'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Requester:</b> {request.employee.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SourceIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Request Id:</b> {request.requestId}
                    </Typography>
                  </Box>
                  {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Journey Date:</b> {dayjs(request.journeyDate).utc('z').local().format("DD/MM/YYYY")} {dayjs(request.departureTime).format("hh:mm a")}
                    </Typography>
                  </Box> */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MyLocationIcon sx={{ mr: 1 }} />
                    <Typography variant="body1"
                      sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        WebkitLineClamp: 2,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal'
                      }}
                    >
                      <b>From:</b> {request.fromLocation}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOnIcon sx={{ mr: 1 }} />
                    <Typography variant="body1"
                      sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        WebkitLineClamp: 2,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal'
                      }}
                    >
                      <b>To:</b> {request.toLocation}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person2Icon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Manager:</b> {request.approvalManager?.name}
                    </Typography>
                  </Box>
                  {request.cancelledBy &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color='warning' sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <b>Cancelled By:</b> {request.cancelledBy.name}
                      </Typography>
                    </Box>
                  }
                  <Box sx={{ width: '100%', overflowX: 'auto', display: 'block' }}>
                    <Stepper activeStep={request.adminApprovedAt || request.adminRejectedAt ? 3 : request.managerApprovedAt ? 2 : 1}>
                      <Step key={'request'} >
                        <StepLabel>Request</StepLabel>
                      </Step>
                      <Step key={'manager'}>
                        <StepLabel {...{ error: (request.managerRejectedAt != null) }}>Manager</StepLabel>
                      </Step>
                      <Step key={'admin'}>
                        <StepLabel {...{ error: (request.adminRejectedAt != null) }}>Admin</StepLabel>
                      </Step>
                    </Stepper>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Mode of Pickup: </b>
                      {getModeOfPickup(request.modeOfPickUp)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FenceIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Gate Pass Type: </b>
                      {request.gatepassType === 0 ? "RGP" : "NRGP"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PinOutlinedIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Gate Pass Number: </b>
                      {request.gatepassNumber}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PriorityHighIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Urgency Level: </b>
                      {urgencyLevelList.find(x => x.id === request.urgencyLevel)?.name || ""}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PinOutlinedIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Type of Material: </b>
                      {materialList.length > 0 ? materialList.find(x => x.id === request.typeOfMaterialId)?.name : ""}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocalShippingIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Courier Type: </b>
                      {request.courierType === 0 ? "Domestic" : "International"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DescriptionIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Purpose of Requirement:</b> {request.purpose}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DateRangeIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Requested At:</b> {dayjs(request.createdAt).format("DD/MM/YYYY hh:mm a")}</Typography>
                  </Box>
                  {request.managerRemarks &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {request.managerApprovedAt ? <GppGoodIcon color='success' sx={{ mr: 1 }} /> : <GppBadIcon color='error' sx={{ mr: 1 }} />}
                      <Typography variant="body2"><b>Manager Remark:</b> {request.managerRemarks}</Typography>
                    </Box>
                  }
                  <Grid container rowSpacing={1} columnSpacing={1}>
                    {request.courierDocument.map((file, index) => (
                      <Grid item key={index}>
                        <Link download={file.document.name} target="_blank" rel="noopener" href={`${process.env.REACT_APP_BASE_URL}/${file.document.path}`}>
                          <Chip
                            icon={getFileIcon(file.document)}
                            label={file.document.name}
                          />
                        </Link>
                      </Grid>
                    ))}
                  </Grid>
                  {
                    request.completedAt == null &&
                    <>
                      {((role == "Admin" && !request.adminApprovedAt && !request.adminRejectedAt) 
                        || (role == "Manager" && !request.managerApprovedAt && !request.managerRejectedAt)) 
                        &&
                        <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, mt: 1 }}>
                          <Button onClick={() => handleApproveOpenDialog(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >
                            Approve
                          </Button>
                          {role != "Admin" 
                            && 
                            <Button onClick={() => handleRejectOpenDialog(request)} variant='contained' color='error' sx={{ flex: '1 1 auto' }} >
                              Reject
                            </Button>
                          }
                        </Box>
                      }
                      <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, }}>
                        {role != "Admin" && !request.managerRejectedAt && !request.cancelledById 
                          && 
                          <Button fullWidth onClick={() => handleCancelRequest(request)} variant='contained' color='warning' sx={{ flex: '1 1 auto' }} >
                            Cancel Request
                          </Button>
                        }
                        {role == "Requester" && !request.managerRejectedAt && !request.cancelledById 
                          && 
                          <Button fullWidth onClick={() => handleCompleteRequest(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >
                            Complete
                          </Button>
                        }
                      </Box>
                    </>
                  }

                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 6 }}>
        <Pagination
          count={Math.ceil(filteredData.length / PAGE_SIZE)}
          page={page}
          siblingCount={1}
          boundaryCount={1}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
      <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: '2rem', right: '2rem' }} onClick={() => navigate('/courier-request/add')}>
        <AddIcon />
      </Fab>
      {role == "Manager" &&
        <ApproveRequestDialogManager open={dialogOpen != null} isApprove={dialogOpen === 'approve'} handleClose={handleCloseDialog} handleApprove={handleApproveManager} />
      }
      {role == "Admin" &&
        <ApproveRequestDialogAdmin open={dialogOpen != null} handleClose={handleCloseDialog} handleApprove={handleApproveAdmin} moduleName={"CourierService"} />
      }
    </Box>
  )
};

export default CourierRequestListingPage